import { AutocompleteV2, Button, OptionTypeV2 } from '@frontend/components/ui';
import {
  useCanvasUpdate,
  useGetCouponOption,
  useGetProjectID,
} from '@frontend/editor/data-access';
import { EditorCtx, UICtx } from '@frontend/editor/external-providers';
import {
  CouponType,
  CouponTypesEnum,
  DrawerTypesEnum,
  ElementTypesEnum,
} from '@frontend/editor/interface';
import { PAGE_COUPON_LIST, usePath } from '@frontend/sorghum/utils';
import { Link, Typography } from '@mui/material';
import { styled } from '@mui/system';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EditorDrawer } from '../editor-drawer/editor-drawer';

const ContainerStyled = styled('div')(({ theme }) => ({
  whiteSpace: 'break-spaces',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  padding: '24px',
}));

const TopWrapperStyled = styled('div')(({ theme }) => ({}));

export const DrawerCoupon = () => {
  const { data: projectID } = useGetProjectID();
  const [t] = useTranslation();
  const { navigateToProjectPage } = usePath();
  const {
    getElement,
    onFocusID,
    setIsDrawerUpdate,
    setIsDrawerFullyUpdate,
    setOnFocusCellID,
  } = useContext(EditorCtx);
  const { setDrawerType, closeModal } = useContext(UICtx);
  const { canvasUpdateData } = useCanvasUpdate();

  const { element } = useMemo(() => {
    const element = getElement(onFocusID) as CouponType;

    return {
      element,
    };
  }, [getElement, onFocusID]);

  const { data: couponOptions } = useGetCouponOption({
    projectID: projectID as string,
  });

  const [couponValue, setCouponValue] = useState<string | null | undefined>(
    null,
  );

  const { title, subtitle1, subtitle2 } = useMemo(() => {
    switch (element?.type) {
      case CouponTypesEnum.SEND:
        return {
          title: t('canvas.coupon.send.title'),
          subtitle1: t('canvas.coupon.send.subtitle1'),
          subtitle2: t('canvas.coupon.send.subtitle2'),
        };
      case CouponTypesEnum.SHOW:
        return {
          title: t('canvas.coupon.show.title'),
          subtitle1: t('canvas.coupon.show.subtitle1'),
          subtitle2: t('canvas.coupon.show.subtitle2'),
        };
      case CouponTypesEnum.REDEEM:
      default:
        return {
          title: t('canvas.coupon.redeem.title'),
          subtitle1: t('canvas.coupon.redeem.subtitle1'),
          subtitle2: t('canvas.coupon.redeem.subtitle2'),
        };
    }
  }, [element?.type, t]);

  const valueOptions = useMemo(() => {
    if (couponOptions) {
      return couponOptions.map((i) => ({
        label: i.name,
        value: i.id,
      }));
    } else {
      return [];
    }
  }, [couponOptions]);

  const isOpen = useMemo(() => {
    return element && element.elementType === ElementTypesEnum.COUPON;
  }, [element]);

  const shouldApplyButtonDisabled = useMemo(() => {
    let shouldDisabled = false;

    if (!couponValue) {
      shouldDisabled = true;
    }
    return shouldDisabled;
  }, [couponValue]);

  const handleApply = useCallback(
    ({
      updatedCouponValue,
    }: {
      updatedCouponValue?: string | null | undefined;
    } = {}) => {
      const usedCouponValue = updatedCouponValue ?? couponValue;

      canvasUpdateData(element?.id, 'couponValue', usedCouponValue);

      setIsDrawerUpdate(false);
      setIsDrawerFullyUpdate(true);
      setOnFocusCellID('');
      setDrawerType(DrawerTypesEnum.CLOSE);
      closeModal();
    },
    [
      couponValue,
      canvasUpdateData,
      element?.id,
      setIsDrawerUpdate,
      setIsDrawerFullyUpdate,
      setOnFocusCellID,
      setDrawerType,
      closeModal,
    ],
  );

  const handleOptionChange = useCallback(
    (_: object, newValue: OptionTypeV2 | null) => {
      if (!newValue) {
        setCouponValue('');
        return;
      }
      setCouponValue(newValue.value);
      setIsDrawerUpdate(true, () =>
        handleApply({
          updatedCouponValue: newValue.value,
        }),
      );
    },
    [setIsDrawerUpdate, handleApply],
  );

  const gotoCouponPage = useCallback(() => {
    navigateToProjectPage({ path: PAGE_COUPON_LIST });
    setDrawerType(DrawerTypesEnum.CLOSE);
  }, [navigateToProjectPage, setDrawerType]);

  useEffect(() => {
    if (element) {
      setCouponValue(element.couponValue);
      setIsDrawerUpdate(false);
    }
  }, [element, setIsDrawerUpdate]);

  useEffect(() => {
    if (shouldApplyButtonDisabled) {
      setIsDrawerFullyUpdate(false);
    } else {
      setIsDrawerFullyUpdate(true);
    }
  }, [shouldApplyButtonDisabled, setIsDrawerFullyUpdate]);

  if (isOpen) {
    return (
      <EditorDrawer drawerType={DrawerTypesEnum.COUPON}>
        <ContainerStyled>
          <TopWrapperStyled>
            <Typography
              sx={{
                mb: '12px',
              }}
              variant="h6"
              color="grey.900"
            >
              {title}
            </Typography>
            <Typography
              sx={{
                mb: '24px',
              }}
              variant="body2"
            >
              {subtitle1}
              <Link onClick={gotoCouponPage} color="info.main">
                {t('canvas.coupon.click')}
              </Link>
              {subtitle2}
            </Typography>
            <AutocompleteV2
              // 必填顯示紅框
              required
              errorMessage={couponValue ? undefined : ' '}
              value={couponValue}
              onChange={handleOptionChange}
              noOptionsText={t('canvas.coupon.empty')}
              placeholder={t('canvas.coupon.placeholder')}
              label={t('canvas.coupon.title')}
              options={valueOptions}
            />
          </TopWrapperStyled>
          <Button
            onClick={() => handleApply()}
            size="large"
            variant="contained"
            fullWidth
            disabled={shouldApplyButtonDisabled}
            sx={{ marginTop: '24px' }}
          >
            {t('common.save')}
          </Button>
        </ContainerStyled>
      </EditorDrawer>
    );
  } else {
    return null;
  }
};

export default memo(DrawerCoupon);
